// -----------------------------------------------------------------------
// <copyright file="TopResult.tsx" company="Microsoft">
//     copyright (c) Microsoft. All rights reserved.
// </copyright>
// -----------------------------------------------------------------------

import {
  Announced,
  classNamesFunction,
  FontSizes,
  Icon,
  IProcessedStyleSet,
  IPropsWithStyles,
  IStyle,
  IStyleSet,
  ITheme,
  Link,
  Stack,
  styled,
  TooltipHost,
} from '@fluentui/react';
import * as React from 'react';
import { CSSProperties, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { AlchemyInsightModel, ContentType } from '../../../web-legacy/models/apiModels';
import { IconNames } from '../../../web-legacy/models/constants/constants';
import { IAppState } from '../../../web-legacy/models/reduxState';
import { openLinksInNewTabSelector } from '../../../web-legacy/packages/app/selectors/contactUsSelector';
import { executeActions } from '../../../web-legacy/packages/contactUs/services/alchemyInsightActionService';
import { sanitizeHTML } from '../../../web-legacy/utilities';
import { StyledText } from '../../common/components/StyledText';
import { appContext } from '../../models/getHelp/appContext';
import { EmeraldUserType } from '../../models/getHelp/emeraldUserType';
import { LogLevel, TagId } from '../../models/getHelp/GetHelpLoggingRequest';
import { ResultType, TopResultData } from '../../models/selfHelp';
import { buildJsllStringForClick, decodeHtmlEntities } from '../../utils/getHelputils';
import { capturePageAction } from '../../utils/oneDsUtil';
import { getString } from '../../utils/stringLoader';
import {
  CardHeight,
  ExpandLinkStyle,
  FeedbackStyle,
  IconToolTipStyle,
  ReadTheArticleLinkStyle,
  resultHeaderStyle,
  ResultTitleStyle,
  shevronIconStyle,
  topResultStackCardStyle,
  LinkTipStyle,
} from '../styles/selfHelpStyles';
import { LinkWithContextMenu } from './LinkWithContextMenu';

interface ITopResultProps extends IPropsWithStyles<null, null> {
  title: string;
  description: string;
  expanded: boolean;
  type: ResultType;
  url: string;
  contentType: ContentType;
  header?: string;
  theme?: ITheme;
  ariaLabel?: string;
  isActionButtonEnabled?: boolean;
  topResult?: TopResultData;
  insights?: Array<AlchemyInsightModel>;
  index?: number;
  showChevronIcon?: boolean;
  openLinksInNewTab?: boolean;
  onClickExpand: (index: number) => void;
  toggleTopResultContent?: (index: number) => void;
}

interface ITopResultStyles extends IStyleSet {
  content?: IStyle;
}

interface ITopResultStyleProps {
  theme: ITheme;
}

const FeedbackTextChange = () => {
  document.getElementById('feedback_text').textContent = getString('GetHelp_ContactSupport_AlchemyGPTFeedbackSubmitted');
}

const getTopResultStyle = (props: ITopResultStyleProps): ITopResultStyles => ({
  content: {
    fontFamily: "'Segoe UI', 'Open Sans', Roboto, 'Helvetica Neue', sans-serif",
    fontSize: FontSizes.medium,
    color: props.theme.semanticColors.bodyText,
    selectors: {
      a: {
        textDecoration: 'none',
        color: props.theme.palette.themeDark,
      },
    },
  },
});

const getClassNames = classNamesFunction<ITopResultStyleProps, ITopResultStyles>();

const TopResultBase = (props: ITopResultProps): JSX.Element => {
  const header: string = props.header || getString('GetHelp_RecommendedSolution');
  let actionLock = false;
  // The Description div element we will use to calculate the height then decide to sure expand button or not
  const descriptionDiv: React.MutableRefObject<HTMLDivElement> = useRef<HTMLDivElement>(null);

  const openLinksInNewTab: boolean = useSelector((state: IAppState) => openLinksInNewTabSelector(state));

  // The state
  const [state, setState] = useState({
    height: 0,
    currentDescription: '',
  });

  const classNames: IProcessedStyleSet<ITopResultStyles> = getClassNames(props.styles!, { theme: props.theme });

  // The 75 is the height of the title and padding part.
  const shouldBeExpandable = (): boolean => state.height > CardHeight - 75;

  const getOverflowStyle = (): CSSProperties => ({
    overflow: shouldBeExpandable() ? 'hidden' : 'auto',
  });

  /**
   * The side effect
   * When the component is mounted or updated, if it's caused by the new description (not equal) we will
   * update the height calculated from the new description div element.
   */
  useEffect(() => {
    if (state.currentDescription !== props.description) {
      setState({
        // If the descriptionDiv.current is falsy, it means the top result is a BCQR, set it to 0
        height: !!descriptionDiv.current ? descriptionDiv.current.offsetHeight : 0,
        // Update the currentDescription with the new one so it won't update until the next query from user
        currentDescription: props.description,
      });
    }
    replaceInsightsBTagWithButton();
  });

  /**
   * logActionExecution
   * @param actionList
   * @param actionSuccessful
   */
  const logActionExecution = (actionList: Array<string>, actionSuccessful: boolean) => {
    const bucketId = props.insights && props.insights.length > 0 ? props.insights[0].BucketId : -1;
    capturePageAction(null, {
      content: {
        contentName: bucketId,
        actionButtonDetails: `${actionList.join(',')};${actionSuccessful}`,
        formnm: 'maxFunnel',
        isContactSuggestedSelfHelp: 'true',
        areaName: 'selfHelpAlchemyInsight',
      },
      behavior: 'SHOW',
    });
  };

  /**
   * Add data-bi attributes to insight links for logging
   * @param element
   * @param bucketId
   * @param index
   */
  const addLoggingAttributesForInsightClick = (element: Element, bucketId: number, index: number): void => {
    element.setAttribute('data-bi-bhvr', 'SHOW');
    element.setAttribute('data-bi-area', 'selfHelpAlchemyInsight');
    element.setAttribute('data-bi-formnm', 'maxFunnel');
    element.setAttribute('data-bi-name', !!bucketId ? bucketId.toString() : '');
    element.setAttribute('data-bi-index', index.toString());
    element.setAttribute('data-bi-iscontactsuggestedselfhelp', 'true');
    if (openLinksInNewTab) {
      element.setAttribute('target', '_blank');
    }
  };

  /**
   * parses and adds LoggingAttributesForInsightClick, updates styling for children elements
   * @param description
   * @param index
   * @returns updated insight
   */
  const updateInsightHTML = (description: string, index: number): string => {
    if (!description) {
      return '';
    }

    let html = description;

    try {
      const htmlDoc: Document = new DOMParser().parseFromString(description, 'text/html');

      if (!!htmlDoc) {
        // In Portico articles, <h2> in section header makes it larger than the Insight header.
        // Replace <h2> with <p><b></b></p>.
        // Example queries:
        // 1. Start a Skype meeting
        // 2. About sync settings on Windows 10 devices
        // 3. Resize the Start menu
        const h2s: NodeListOf<HTMLHeadingElement> = htmlDoc.querySelectorAll('h2');
        for (let i = 0; i < h2s.length; i++) {
          const b = document.createElement('b');
          h2s[i].childNodes.forEach(childNode => {
            h2s[i].removeChild(childNode);
            b.appendChild(childNode);
          });
          const p = document.createElement('p');
          p.appendChild(b);
          h2s[i].parentNode.replaceChild(p, h2s[i]);
        }

        // Insight links: add logging attributes
        const anchors: NodeListOf<HTMLAnchorElement> = htmlDoc.querySelectorAll('a');
        // Todo: Brook may need to revisit getting bucket when we have more than one insight
        const bucketId: number = props.insights && props.insights.length ? props.insights[0].BucketId : -1;
        if (!!anchors) {
          for (let i = 0; i < anchors.length; i++) {
            addLoggingAttributesForInsightClick(anchors[i], bucketId, index);
          }
        }

        // Todo: As of 08/23, we've confirmed that Portico articles have been moved to SOC and can be removed
        // Action buttons in Portico articles: add logging attributes and css class
        // Input example: <button onclick="window.location.href = 'ms-settings:sync?activationSource=SMC-IA-4026102'">Open Sync settings</button>
        const porticoButtons: NodeListOf<Element> = htmlDoc.querySelectorAll('button[onclick^="window.location.href"]');
        if (!!porticoButtons) {
          for (let i = 0; i < porticoButtons.length; i++) {
            addLoggingAttributesForInsightClick(porticoButtons[i], bucketId, index);
            porticoButtons[i].classList.add('btn', 'btn-primary', 'sharp');
          }
        }

        // IE does not support firstElementChild, in that case use firstChild instead.
        const firstChild: Element = !!htmlDoc.firstElementChild
          ? htmlDoc.firstElementChild
          : (htmlDoc.firstChild as Element);
        html = firstChild.innerHTML;
      }
    } catch (error) {
      console.log('Failed to update insight html.', error);
    }

    return html;
  };

  /**
   *
   * @param event
   * @returns
   */
  const insightActionClickHandler = async (event: Event): Promise<void> => {
    if (actionLock) {
      return;
    }

    actionLock = true;
    const actions: string[] = (event.target as HTMLElement).getAttribute('data-action-id')?.split('|') || [];

    try {
      const result: boolean = await executeActions(actions);
      actionLock = false;
      logActionExecution(actions, result);
    } catch (error) {
      actionLock = false;
      logActionExecution(actions, false);
    }
  };

  /**
   *
   * @returns
   */
  const replaceInsightsBTagWithButton = (): void => {
    let index;

    if (
      props.type !== ResultType.Insight ||
      !props.isActionButtonEnabled ||
      !descriptionDiv ||
      !descriptionDiv.current
    ) {
      return;
    }
    // Convert b tags inside insight, to action buttons
    const bElementList: NodeListOf<Element> = descriptionDiv.current.querySelectorAll('b[data-action-id]');
    for (index = 0; index < bElementList.length; index++) {
      const convertedString = bElementList[index].outerHTML.replace(
        /(?:<)(\w+)(\b.*)(?:<\/)\1(?:>)/,
        '<button$2</button>'
      );
      const convertedHtml: Document = new DOMParser().parseFromString(convertedString, 'text/html');
      bElementList[index].parentNode.replaceChild(convertedHtml.body.firstElementChild, bElementList[index]);
    }

    // Add ocpActionLink class and onClick handler to insight action buttons
    const buttonElementList: HTMLButtonElement[] = descriptionDiv.current.querySelectorAll(
      'button[data-action-id]'
    ) as unknown as HTMLButtonElement[];
    for (index = 0; index < buttonElementList.length; index++) {
      buttonElementList[index].classList.add('ocpActionLink');
      buttonElementList[index].onclick = insightActionClickHandler;
    }
  };

  /**
   * If this is true, means we got a new Insight description, we just update DOM to render the description so we
   * can calculate its height for next render (the real one user will see). Once this render is done, it will
   * trigger useEffect() to setState to make another render.
   */
  // Todo: Brook - ask michael if we should update title as well when insight changes and if we should handle multiple insights and updateInsightHTML logic
  if (props.type === ResultType.Insight && state.currentDescription !== props.description) {
    console.log(`|||| ${props.description}`);
    return (
      <div
        ref={(el: HTMLDivElement) => (descriptionDiv.current = el)}
        className={classNames.content}
        dangerouslySetInnerHTML={{ __html: props.description }} // eslint-disable-line
      />
    );
  }

  const onClickExpand = () => {
    const index = !!props.index ? props.index : 0;
    return props.onClickExpand(index);
  };

  /**
   *
   * @param index
   */
  const toggleShowContent = (index: number) => {
    if (props.toggleTopResultContent) {
      props.toggleTopResultContent(index);
    }
  };

  /**
   * If we come to here, either the top result is not Insight type or we already got the height of the Insight
   * description. So we will just render the correct layout for users.
   */
  let content: JSX.Element = null;
  const narratorMessage: string = header + '. ' + props.title;
  const { topResult } = props;
  const hideContent: boolean = topResult ? topResult.hideContent : false;
  const showChevronIcon: boolean = props.showChevronIcon;
  const index = props.index || 0;
  const allowedAttrs: string[] = props.openLinksInNewTab ? ['target'] : undefined;
  switch (props.type) {
    case ResultType.BCQR:
      descriptionDiv.current = null;
      const narratorLabel = getString('GetHelp_ReadArticleLink') + '. ' + props.title;
      content = (
        <Stack styles={topResultStackCardStyle(props.theme.palette.neutralLight)}>
          <StyledText styles={ResultTitleStyle}>{decodeHtmlEntities(props.title)}</StyledText>
          <StyledText>{decodeHtmlEntities(props.description)}</StyledText>
          <LinkWithContextMenu
            styles={ReadTheArticleLinkStyle(props.theme.palette.themeDark)}
            url={props.url}
            text={getString('GetHelp_ReadArticleLink')}
            ariaLabel={narratorLabel}
            sendAlchemyFeedback
            jsllMessage={buildJsllStringForClick(
              'Top results:Read the article',
              appContext.locale,
              appContext.sessionId,
              props.title,
              EmeraldUserType[appContext.emeraldUserType]
            )}
            eventMessage={{
              TagId: TagId.ClickedBcqrReadArticle,
              LogLevel: LogLevel.Info,
              Message: 'Clicked BCQR Read article',
              Adhoc0: appContext.locale,
              Adhoc1: props.url,
              Adhoc2: props.title,
              Adhoc3: ContentType[props.contentType],
              Adhoc5: EmeraldUserType[appContext.emeraldUserType],
            }}
            sendAlchemyGPTFeedback={false}
          />
        </Stack>
      );
      break;
    case ResultType.Insight:
      console.log(`|||| ${props.description}`);
      content = (
        <Stack
          styles={topResultStackCardStyle(
            props.theme.palette.neutralLight,
            shouldBeExpandable() ? props.expanded : true
          )}
        >
          <div style={{ display: 'flex' }}>
            <StyledText styles={ResultTitleStyle}>{decodeHtmlEntities(props.title)}</StyledText>
            {showChevronIcon && (
              <Icon
                styles={shevronIconStyle()}
                iconName={!hideContent ? IconNames.ChevronUp : IconNames.ChevronDown}
                onClick={() => toggleShowContent(index)}
              />
            )}
          </div>
          {!hideContent && (
            <>
              <div
                ref={(el: HTMLDivElement) => (descriptionDiv.current = el)}
                className={classNames.content}
                style={getOverflowStyle()}
                dangerouslySetInnerHTML={sanitizeHTML(updateInsightHTML(props.description, index), allowedAttrs)} // eslint-disable-line
              />
              {shouldBeExpandable() && (
                <Link
                  styles={
                    props.expanded
                      ? ReadTheArticleLinkStyle(props.theme.palette.themeDark)
                      : ExpandLinkStyle(props.theme.palette.neutralLight, props.theme.palette.themeDark)
                  }
                  onClick={onClickExpand}
                  aria-label={
                    (props.expanded ? getString('GetHelp_CollapseLinkText') : getString('GetHelp_ExpandLinkText')) +
                    ' ' +
                    header
                  }
                  data-m={buildJsllStringForClick(
                    'Top results:Expand/Collapse',
                    appContext.locale,
                    appContext.sessionId,
                    props.title,
                    EmeraldUserType[appContext.emeraldUserType]
                  )}
                >
                  {props.expanded ? getString('GetHelp_CollapseLinkText') : getString('GetHelp_ExpandLinkText')}
                </Link>
              )}
            </>
          )}
        </Stack>
      );
      break;
    case ResultType.AlchemyGPT:
      descriptionDiv.current = null;
      const narratorLabelGpt = getString('GetHelp_ReadArticleLink') + '. ' + getString('GetHelp_ContactSupport_AlchemyGPT');
      content = (
        <Stack styles={topResultStackCardStyle(props.theme.palette.neutralLight)}>
          <StyledText styles={ResultTitleStyle}>{getString('GetHelp_ContactSupport_AlchemyGPT')}
            <TooltipHost content={decodeHtmlEntities(getString('GetHelp_ContactSupport_AlchemyGptToolTip'))}>
              <Icon iconName="Info" styles={IconToolTipStyle}></Icon>
            </TooltipHost>
          </StyledText>
          <div
            ref={(el: HTMLDivElement) => (descriptionDiv.current = el)}
            className={classNames.content}
            style={getOverflowStyle()}
            dangerouslySetInnerHTML={sanitizeHTML(props.description)} // eslint-disable-line
          />
          <StyledText id='feedback_text' styles={FeedbackStyle}>{getString('GetHelp_ContactSupport_AlchemyGPTFeedback')}
            <LinkWithContextMenu
              onClick={FeedbackTextChange}
              url={props.url}
              text="Yes"
              ariaLabel={narratorLabelGpt}
              sendAlchemyFeedback
              jsllMessage={buildJsllStringForClick(
                'Top results:Read the article',
                appContext.locale,
                appContext.sessionId,
                props.title,
                EmeraldUserType[appContext.emeraldUserType]
              )}
              sendAlchemyGPTFeedback={true}
              styles={LinkTipStyle}
            />
            <LinkWithContextMenu
              onClick={FeedbackTextChange}
              url={props.url}
              text="No"
              ariaLabel={narratorLabelGpt}
              sendAlchemyFeedback
              jsllMessage={buildJsllStringForClick(
                'Top results:Read the article',
                appContext.locale,
                appContext.sessionId,
                props.title,
                EmeraldUserType[appContext.emeraldUserType]
              )}
              sendAlchemyGPTFeedback={true}
              styles={LinkTipStyle}
            />
          </StyledText>
        </Stack>
      );
      break;
  }
  return (
    <>
      {index === 0 && <StyledText styles={resultHeaderStyle}>{header}</StyledText>}
      {content}
      <Announced message={narratorMessage} />
    </>
  );
};

export const TopResult: React.FunctionComponent<ITopResultProps> = styled(
  TopResultBase,
  getTopResultStyle,
  null,
  null,
  true
);
